import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import * as Sentry from '@sentry/nextjs';
import { GetStaticPathsContext, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { PHASE_DEVELOPMENT_SERVER, PHASE_PRODUCTION_BUILD } from 'next/constants';
import { Article, WithContext } from 'schema-dts';

import { CHANNEL, newsSlugs } from '@boss/constants/corporate-jobs';
import { newsArticleRepository } from '@boss/contentful-client/corporate-jobs';
import { news } from '@boss/services';
import { Environment, RichTextFields, SectionFields } from '@boss/types/corporate-jobs';

import { SEO } from '../../components';
import { PageTypes } from '../../constants/page';
import { NewsDetailPage } from '../../containers';
import Layout from '../../layout/default';
import { getServerSideTranslations } from '../../utils';
import { mapNextToContentfulLocaleMarket } from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';
import { showNewsPages } from '../../utils/options';
// TODO: Develop more. It seems the PHASE_PRODUCTION_BUILD gives a false positive
// due to environment embedding
const IS_BUILD =
  (process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD && !process.env.SKIP_PRODUCT_BUILD) ||
  (process.env.NEXT_PHASE === PHASE_DEVELOPMENT_SERVER && process.env.DEBUG_BUILD === 'true');

const IS_DEV = process.env.NEXT_PHASE === PHASE_DEVELOPMENT_SERVER;

/**
 * Takes the article content, concat all the content richtext into to a single string
 * This is used in structured data so it can be crawled
 */
const articleContentToPlainText = (contents: SectionFields[]): string => {
  const textArr = [];

  for (const content of contents) {
    if (content?.content) {
      textArr.push(
        content.content
          .filter((item): item is RichTextFields => item && item.__typename === 'richText')
          .map(text => (text?.content ? documentToPlainTextString(text.content) : '')),
      );
    }
  }

  return textArr.join(' ');
};

export const getStaticPaths = async ({ locales }: GetStaticPathsContext) => {
  try {
    const paths = [];

    if (!showNewsPages) {
      return {
        paths: [],
        fallback: false,
      };
    }

    if (IS_BUILD && CHANNEL && locales) {
      for (const locale of locales.filter((locale: string) => locale !== 'default')) {
        const contentfulLocaleMarket = mapNextToContentfulLocaleMarket(locale);

        for (const newsArticle of await news.getAll(contentfulLocaleMarket)) {
          paths.push({ locale, params: { slug: newsArticle.slug, id: newsArticle.id } });
        }
      }
    }

    return {
      paths,
      fallback: IS_DEV ? 'blocking' : false,
    };
  } catch (error) {
    Sentry.captureException(error, {
      tags: {
        type: 'Fetching newsarticle paths',
      },
    });

    return {
      paths: [],
      fallback: false,
    };
  }
};

export const getStaticProps = async ({
  preview,
  params,
  locale,
}: GetStaticPropsContext<{ id: string; slug: string }>) => {
  const slug = params?.slug;

  if (!slug || !locale || locale === 'default' || !CHANNEL) {
    return {
      notFound: true,
    };
  }

  const contentfulLocaleMarket = mapNextToContentfulLocaleMarket(locale);

  try {
    const [navigation, newsArticle] = await Promise.all([
      getMappedNavigation(contentfulLocaleMarket, !!preview),
      news.getBySlug(slug, contentfulLocaleMarket),
    ]);

    if (!newsArticle) {
      return {
        notFound: true,
      };
    }

    const relatedArticles = await newsArticleRepository.getRelatedArticles({
      refCreationDate: newsArticle.publicationDate,
      locale: contentfulLocaleMarket,
      entryId: newsArticle.id,
      channel: CHANNEL,
    });

    const slugEnrichedRelatedArticles = relatedArticles.map(article => ({
      ...article,
      slug: article.slug,
    }));

    return {
      props: {
        pageType: PageTypes.NEWS_DETAIL,
        preview: !!preview,
        navigation,
        slug,
        locale,
        newsArticle,
        relatedArticles: slugEnrichedRelatedArticles,
        environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
        ...(await getServerSideTranslations(locale, ['common', 'news'])),
      },
    };
  } catch (error) {
    console.error(`[news/slug] getStaticProps failed for slug "${slug}":`, error);
    return { notFound: true };
  }
};

export const NewsArticle = ({
  environment,
  locale,
  navigation,
  newsArticle,
  preview,
  relatedArticles,
  slug,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { metaTitle, metaKeywords, metaDescription, image } = newsArticle;

  const structuredData: WithContext<Article> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    articleBody: articleContentToPlainText(newsArticle.content),
    author: {
      '@type': 'Person',
      name: newsArticle.author,
    },
    headline: newsArticle.metaTitle,
    description: newsArticle.metaDescription ?? '',
    datePublished: new Date(newsArticle.publicationDate).toISOString(),
    image: newsArticle?.image.image.file?.url,
  };

  return (
    <SEO
      description={metaDescription}
      imageSrc={image?.image?.file?.url}
      localizedSlugs={newsSlugs}
      metaKeywords={metaKeywords}
      navigation={navigation}
      noFollow
      noIndex
      slug={slug}
      structuredData={[structuredData]}
      title={metaTitle}
    >
      <Layout environment={environment} localizedSlugs={newsSlugs} navigation={navigation} preview={preview}>
        <NewsDetailPage
          locale={locale}
          metaTitle={metaTitle}
          newsArticle={newsArticle}
          relatedArticles={relatedArticles}
        />
      </Layout>
    </SEO>
  );
};

export default NewsArticle;
